import React, { useState, useEffect, useRef } from 'react';

const BET_AMOUNTS = [0.01, 0.1, 1] as const;
const SELL_PERCENTAGES = [10, 50, 100] as const;

interface AmountSelectorProps {
  selectedAmount: number | null;
  onAmountSelect: (amount: number) => void;
  disabled?: boolean;
  isDoubleDownMode?: boolean;
  isSellMode?: boolean;
}

export const AmountSelector: React.FC<AmountSelectorProps> = ({
  selectedAmount,
  onAmountSelect,
  disabled = false,
  isDoubleDownMode = false,
  isSellMode = false
}) => {
  const amounts = isSellMode ? SELL_PERCENTAGES : BET_AMOUNTS;
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Local display string so user can type freely (e.g. "0." without snapping)
  const [displayValue, setDisplayValue] = useState<string>('');

  // Track whether the input has a custom (non-preset) value
  const isCustomValue = displayValue !== '' && !amounts.some(a => a === parseFloat(displayValue));

  // Sync display string when selectedAmount changes externally (mode switch, etc.)
  useEffect(() => {
    if (selectedAmount === null || selectedAmount === 0) {
      setDisplayValue('');
    } else {
      const parsed = parseFloat(displayValue);
      if (isNaN(parsed) || parsed !== selectedAmount) {
        setDisplayValue(String(selectedAmount));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAmount, isSellMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    if (isSellMode) {
      if (raw === '') {
        setDisplayValue('');
        onAmountSelect(0);
        return;
      }
      const digitsOnly = raw.replace(/[^0-9]/g, '');
      if (digitsOnly === '') return;
      
      const num = parseInt(digitsOnly, 10);
      if (num > 100) {
        setDisplayValue('100');
        onAmountSelect(100);
      } else {
        setDisplayValue(digitsOnly);
        if (num > 0) {
          onAmountSelect(num);
        }
      }
    } else {
      if (raw === '') {
        setDisplayValue('');
        onAmountSelect(0);
        return;
      }
      let cleaned = raw.replace(/[^0-9.]/g, '');
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }
      
      setDisplayValue(cleaned);
      
      const num = parseFloat(cleaned);
      if (!isNaN(num) && num > 0) {
        onAmountSelect(num);
      }
    }
  };

  const handleInputBlur = () => {
    if (displayValue === '') return;
    
    if (isSellMode) {
      const num = parseInt(displayValue, 10);
      if (isNaN(num) || num <= 0) {
        setDisplayValue('');
        onAmountSelect(0);
      } else {
        const clamped = Math.min(100, Math.max(1, num));
        setDisplayValue(String(clamped));
        onAmountSelect(clamped);
      }
    } else {
      const num = parseFloat(displayValue);
      if (isNaN(num) || num <= 0) {
        setDisplayValue('');
        onAmountSelect(0);
      } else {
        const clamped = Math.max(0.001, num);
        const formatted = parseFloat(clamped.toFixed(6)).toString();
        setDisplayValue(formatted);
        onAmountSelect(clamped);
      }
    }
  };

  const handlePillClick = (amount: number) => {
    // Toggle off if clicking the already-selected preset
    if (selectedAmount === amount) {
      setDisplayValue('');
      onAmountSelect(0);
    } else {
      setDisplayValue(String(amount));
      onAmountSelect(amount);
    }
  };

  return (
    <div className="amount-pills-container">
      <div className="amount-pills-wrapper">
        {/* Preset pills */}
        {amounts.map(amount => (
          <button
            key={amount}
            className={`amount-pill ${selectedAmount === amount ? 'selected' : ''} ${disabled ? 'locked' : ''} ${isDoubleDownMode && selectedAmount === amount ? 'double-down' : ''}`}
            onClick={() => handlePillClick(amount)}
            disabled={disabled}
          >
            {isSellMode ? `${amount}%` : `${amount} SOL`}
          </button>
        ))}

        {/* Custom input */}
        <div className={`amount-pill amount-pill-input ${isCustomValue ? 'has-value' : ''} ${disabled ? 'locked' : ''}`}>
          <input
            ref={inputRef}
            type="text"
            inputMode={isSellMode ? 'numeric' : 'decimal'}
            className="amount-custom-input"
            value={displayValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            placeholder={isSellMode ? 'Custom %' : 'Custom'}
            disabled={disabled}
          />
          <span className="amount-pill-suffix">
            {isSellMode ? '%' : 'SOL'}
          </span>
        </div>
      </div>
    </div>
  );
};
